import { isUndefined, omitBy } from "lodash";
import { TaggerAction } from "store/actions";
import { IAction } from "store/actions/i-action";

import { immutablyChangeValue } from "../../../utils/immutable";
import { CurrencyType, IProcessedTag, ISetTagsPayload, TaggerDocument } from "../../types";
import { ITaggerDocument } from "../../types/tagger/api";
import { reducer } from "../index";

reducer.createHandler(TaggerAction.ChangeDocumentData, (state, { payload }) => {
  return immutablyChangeValue(state, ["tagger", "documents", payload.docId, "data", payload.field], payload.value);
});

reducer.createHandler(TaggerAction.ChangeDocumentFileData, (state, { payload }) => {
  return immutablyChangeValue(state, ["tagger", "documents", payload.docId, "fileData", payload.field], payload.value);
});

reducer.createHandler(TaggerAction.AddFiles, (state, { payload }) => {
  const taggerState = { ...state.tagger };
  const documents = payload.documents as ITaggerDocument[];

  documents?.forEach((resDoc, index) => {
    const file = payload.files[index] as File;
    const folder = payload.folders[index] || 0;
    const doc: TaggerDocument = {
      id: resDoc.id,
      file,
      polygons: null,
      error: null,
      dealId: resDoc.deal_id,
      isSplittable: resDoc.is_splittable,
      documentSeriesId: resDoc.series_id,
      gptAnalysisLoading: false,
      fileData: { ...resDoc.file_data },
      data: {
        selectedFolder: folder,
        selectedRound: resDoc.round_id,
        selectedInvestor: resDoc.investor_id,
        selectedCurrency: resDoc.currency as CurrencyType,
        selectedDate: resDoc.date
      },
      currentPage: 1
    };

    if (!taggerState.documentIds.includes(doc.id)) {
      taggerState.documentIds = [...taggerState.documentIds, doc.id];
    }
    taggerState.documents = { ...taggerState.documents, [doc.id]: doc };
  });

  return { ...state, tagger: taggerState };
  //
  // documents?.forEach((doc) => {
  //   if (Array.isArray(doc.gpt_results)) {
  //     newState = fileAnalysisSuccessHandler(newState, {
  //       payload: { docId: doc.id, analysisResults: doc.gpt_results }
  //     });
  //   }
  // });
  //
  // return newState;
});

reducer.createHandler(TaggerAction.UpdatePolygons, (state, { payload }) => {
  if (!state.tagger.documents[payload.doc]) {
    return state;
  }
  return immutablyChangeValue(state, ["tagger", "documents", payload.doc, "polygons"], payload.polygons);
});

reducer.createHandler(TaggerAction.AddTagSuccess, (state, { payload }) => {
  const { tempId, tagId, tagData, value } = payload;
  const tagIndex = state.tagger.tags.findIndex((tag) => tag.id === tempId);
  if (tagIndex > -1) {
    const currTagData = state.tagger.tags[tagIndex];
    let newState = immutablyChangeValue(state, ["tagger", "tags", tagIndex, "id"], tagId);

    // add value from api
    newState = immutablyChangeValue(newState, ["tagger", "tags", tagIndex, "value"], value);

    if (tagData) {
      const newTagData = { ...currTagData, ...tagData };
      return immutablyChangeValue(newState, ["tagger", "tags", tagIndex, "data"], newTagData);
    }
    return newState;
  }
  return state;
});

reducer.createHandler(TaggerAction.AddTag, (state, { payload }) => {
  return immutablyChangeValue(state, ["tagger", "tags", state.tagger.tags.length], payload.tag);
});

reducer.createHandler(TaggerAction.AddTags, (state, { payload }: IAction<{ tags: IProcessedTag[] }>) => {
  const newTags = [...state.tagger.tags, ...payload.tags];
  return immutablyChangeValue(state, ["tagger", "tags"], newTags);
});

reducer.createHandler(TaggerAction.FilesAnalyzingStatus, (state, { payload }) => {
  if (payload === "finished" && state.tagger.analyzingStatus === "stopped") {
    return state;
  }
  return immutablyChangeValue(state, ["tagger", "analyzingStatus"], payload);
});

reducer.createHandler(TaggerAction.SetTags, (state, { payload }: IAction<ISetTagsPayload>) => {
  let keepTags: IProcessedTag[] = [];
  if (payload.dealId && payload.seriesId) {
    keepTags = state.tagger.tags.filter((tag) => tag.deal.id !== payload.dealId && tag.series_id !== payload.seriesId);
  } else if (payload.docId) {
    keepTags = state.tagger.tags.filter((tag) => tag.document_id !== payload.docId);
  } else if (payload.dealId) {
    keepTags = state.tagger.tags.filter((tag) => !!tag.document_id);
  }

  return immutablyChangeValue(state, ["tagger", "tags"], [...keepTags, ...payload.tags]);
});

reducer.createHandler(TaggerAction.EditTag, (state, { payload }) => {
  const tagIndex = state.tagger.tags.findIndex(
    (tag) => tag.id === payload.tag.id || tag.split_id === payload.tag.split_id
  );
  const tag = state.tagger.tags[tagIndex];
  const cleanedTag = omitBy(payload.tag, isUndefined);
  const cleanedTagData = payload.tag && omitBy(payload.tag.data, isUndefined);
  const newTag = { ...tag, ...cleanedTag, ...cleanedTagData };
  return tagIndex > -1 ? immutablyChangeValue(state, ["tagger", "tags", tagIndex], newTag) : state;
});

reducer.createHandler(TaggerAction.DeleteTag, (state, { payload }) => {
  const tagIndex = state.tagger.tags.findIndex((tag) => tag.id === payload.tagId);
  const tag = { ...state.tagger.tags[tagIndex], isDeleted: true, needsSync: true };
  return tagIndex > -1 ? immutablyChangeValue(state, ["tagger", "tags", tagIndex], tag) : state;
});

reducer.createHandler(TaggerAction.ResetTagger, (state) => ({
  ...state,
  tagger: {
    documentIds: [],
    documents: {},
    tags: [],
    analyzingStatus: "pending"
  }
}));
